// src/components/AssignmentsPanel.js
//
// G8.2 (2026-09-14, task list section 223): src\data\assignments.js - built 2026-08-29 for ruling 17
// and green in TST-1 (65 assertions) - reaches a screen. One component, two seats:
//   mode="issuer"   the General Chairperson's desk: issue an assignment to one of his Local
//                   Chairpersons (the seats published in committees/{committee}.seats), watch it
//                   move, close it. Ruling 17 / SS5.2: the subject is FREE TEXT - there is no list.
//   mode="assignee" the Local Chairperson's desk: what the GC has given him; take it up, answer it.
// Every rule of who-may-do-what is the module's (mayIssue, acknowledgePatch, respondPatch,
// closeAssignment); this file draws and writes, it decides nothing. The Firestore rules for
// /assignments were written with the module and hold the same line server-side.
import React, { useEffect, useMemo, useState } from 'react';
import { db } from '../firebase';
import RulingRef from './RulingRef';   // ruling 168: the number behind Why? for a member, [R-nn] for an officer
import { collection, query, where, onSnapshot, addDoc, doc, updateDoc, getDoc } from 'firebase/firestore';
import {
  STATE_LABELS, mayIssue, issueAssignment, acknowledgePatch, respondPatch, closeAssignment,
  partitionAssignments, daysUntilDue, seatBoxState, SEAT_BOX,
} from '../data/assignments';

const C = { panel: '#0B1626', line: '#2E4A6B', dim: '#8AA4BF', accent: '#E8A020', bad: '#E06C6C', ok: '#5FB878', text: '#DCE7F2' };

function dueLabel(a) {
  const d = daysUntilDue(a);
  if (d === null) return 'no due date on record';
  if (d < 0) return Math.abs(d) + ' day(s) overdue';
  if (d === 0) return 'due today';
  return 'due in ' + d + ' day(s)';
}

function Row({ x, user, mode, onOpenClaim, onChanged }) {
  const a = x.assignment;
  const [response, setResponse] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const write = async (patchFn, input) => {
    setErr(''); setBusy(true);
    const r = patchFn(a, user, input);
    if (!r.ok) { setErr(r.errors.join(' ')); setBusy(false); return; }
    try { await updateDoc(doc(db, 'assignments', a.id), r.patch); if (onChanged) onChanged(); }
    catch (e) { setErr('Not saved: ' + (e && e.code ? e.code : e.message)); }
    setBusy(false);
  };
  const state = STATE_LABELS[a.state] || a.state;
  const tone = x.overdue ? C.bad : (a.state === 'responded' ? C.ok : C.accent);
  return (
    <div style={{ borderTop: '1px solid ' + C.line, padding: '10px 0' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'baseline' }}>
        <div style={{ color: C.text, fontSize: 13, fontWeight: 700 }}>{a.subject}</div>
        <div style={{ color: tone, fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>{state} · {dueLabel(a)}</div>
      </div>
      <div style={{ fontSize: 11, color: C.dim, marginTop: 2 }}>
        {mode === 'issuer' ? 'To ' + a.assigneeName : 'From ' + a.issuerName} · issued {String(a.issuedAt || '').slice(0, 10)}
        {a.subjectClaimId ? <> · <button onClick={() => onOpenClaim && onOpenClaim(a.subjectClaimId)} style={{ background: 'none', border: 'none', color: C.accent, cursor: 'pointer', padding: 0, fontSize: 11, textDecoration: 'underline' }}>open the claim</button></> : null}
      </div>
      {a.body ? <div style={{ fontSize: 12, color: C.text, marginTop: 6, whiteSpace: 'pre-wrap' }}>{a.body}</div> : null}
      {a.response ? <div style={{ fontSize: 12, color: C.text, marginTop: 6, padding: '6px 8px', background: '#0F1F33', border: '1px solid ' + C.line }}><span style={{ color: C.dim }}>Answer ({String(a.respondedAt || '').slice(0, 10)}): </span>{a.response}</div> : null}
      {a.state === 'closed' ? <div style={{ fontSize: 11, color: C.dim, marginTop: 4 }}>Closed {String(a.closedAt || '').slice(0, 10)} by {a.closedBy}{a.closeNote ? ' - ' + a.closeNote : ''}</div> : null}
      {err ? <div style={{ color: C.bad, fontSize: 12, marginTop: 6 }}>&#9888; {err}</div> : null}

      {mode === 'assignee' && a.state === 'issued' && (
        <button className="btn btn-secondary btn-sm" style={{ marginTop: 8 }} disabled={busy} onClick={() => write(acknowledgePatch, {})}>Take it up</button>
      )}
      {mode === 'assignee' && (a.state === 'issued' || a.state === 'acknowledged') && (
        <div style={{ marginTop: 8 }}>
          <textarea aria-label="What was done, or what stopped it" className="form-input" rows={3} placeholder="What was done, or what stopped it" value={response} onChange={e => setResponse(e.target.value)} style={{ width: '100%' }} />
          <button className="btn btn-primary btn-sm" style={{ marginTop: 6 }} disabled={busy || !response.trim()} onClick={() => write(respondPatch, { response })}>Answer</button>
        </div>
      )}
      {mode === 'issuer' && a.state !== 'closed' && (
        <div style={{ marginTop: 8, display: 'flex', gap: 8, alignItems: 'center' }}>
          <input aria-label="Closing note (optional)" className="form-input" placeholder="Closing note (optional)" value={note} onChange={e => setNote(e.target.value)} style={{ flex: 1 }} />
          <button className="btn btn-secondary btn-sm" disabled={busy} onClick={() => write(closeAssignment, { note })}>Close</button>
        </div>
      )}
    </div>
  );
}

export default function AssignmentsPanel({ user, mode, onOpenClaim }) {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState('');
  const [seats, setSeats] = useState(null);
  const [seatsErr, setSeatsErr] = useState('');   // forensic audit 2026-10-16
  const [form, setForm] = useState({ assigneeUid: '', subject: '', body: '', dueOn: '', subjectClaimId: '' });
  const [formErr, setFormErr] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState('');

  useEffect(() => {
    if (!user || !user.uid) return undefined;
    const field = mode === 'issuer' ? 'issuerUid' : 'assigneeUid';
    const q = query(collection(db, 'assignments'), where(field, '==', user.uid));
    const unsub = onSnapshot(q, snap => { setRows(snap.docs.map(d => ({ id: d.id, ...d.data() }))); setErr(''); },
      e => { setErr('Assignments could not be read: ' + (e && e.code ? e.code : e.message)); setRows([]); });
    return () => unsub();
  }, [user && user.uid, mode]);

  useEffect(() => {
    if (mode !== 'issuer' || !user || !user.committee) return undefined;
    let dead = false;
    getDoc(doc(db, 'committees', user.committee)).then(s => {
      if (dead) return;
      const list = (s.exists() && Array.isArray(s.data().seats)) ? s.data().seats.filter(x => x.role === 'local_chair') : [];
      setSeats(list); setSeatsErr('');
    // Forensic audit 2026-10-16: a failed read used to become [] - "No Local Chairperson seat with an account is
    // published". It is now said for what it is, and nothing can be issued until the seats are read.
    }).catch((e) => { if (!dead) setSeatsErr('The committee\'s seats could not be read (' + ((e && (e.code || e.message)) || 'refused') + '). This is NOT "no seat published" - reload the page.'); });
    return () => { dead = true; };
  }, [mode, user && user.committee]);

  const p = useMemo(() => partitionAssignments(rows || []), [rows]);

  const issue = async () => {
    setFormErr([]); setSaved('');
    const seat = (seats || []).find(s => s.uid === form.assigneeUid);
    const r = issueAssignment(user, { ...form, assigneeName: seat ? seat.name : '' });
    if (!r.ok) { setFormErr(r.errors); return; }
    setSaving(true);
    try {
      await addDoc(collection(db, 'assignments'), r.record);
      setForm({ assigneeUid: '', subject: '', body: '', dueOn: '', subjectClaimId: '' });
      setSaved('Issued to ' + r.record.assigneeName + '.');
    } catch (e) { setFormErr(['Not saved: ' + (e && e.code ? e.code : e.message)]); }
    setSaving(false);
  };

  const title = mode === 'issuer' ? 'Assignments you have given' : 'Assignments from your General Chairperson';
  const count = err ? 'could not read' : rows === null ? '…' : (p.open.length + ' open' + (p.overdue.length ? ', ' + p.overdue.length + ' overdue' : ''));
  return (
    <div style={{ border: '1px solid ' + C.line, background: C.panel, marginBottom: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '10px 14px', borderBottom: '1px solid ' + C.line }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{title}</div>
          <div style={{ fontSize: 11, color: C.dim, marginTop: 2 }}>"Any other assignments the GC may give to the LC" - issuer, assignee, subject, due date, state, response. The subject is written, not picked from a list.<RulingRef user={user} n={17} why="A General Chairperson may give a Local Chairperson any other assignment, and the platform records it." /></div>
        </div>
        <div style={{ fontSize: 12, fontWeight: 700, color: (err || p.overdue.length) ? C.bad : C.ok, whiteSpace: 'nowrap' }}>{count}</div>
      </div>
      <div style={{ padding: '8px 14px 12px', fontSize: 12, color: C.dim }}>
        {err ? <div style={{ color: C.bad }}>&#9888; {err}</div> : null}

        {mode === 'issuer' && mayIssue(user) && (
          <div style={{ marginBottom: 10, paddingBottom: 10, borderBottom: '1px solid ' + C.line }}>
            <div style={{ color: C.text, fontWeight: 700, marginBottom: 6 }}>Give an assignment</div>
            {/* MASTER LIST 10.8: the administrator holds no committee, so there are no seats to load - say so
                instead of waiting forever (seatBoxState, tested in test-assignments.mjs). */}
            {seatBoxState(user, seats) === SEAT_BOX.NO_COMMITTEE ? (
              <div style={{ color: C.accent }}>You're signed in as the administrator, and the administrator isn't part of any committee, so there's no one here to assign work to. Sign in as a General Chairperson to give an assignment.</div>
            ) : seatsErr ? <div style={{ color: C.bad }}>&#9888; {seatsErr}</div>
            : seats === null ? <div>Loading the committee's seats…</div> : seats.length === 0 ? (
              <div style={{ color: C.accent }}>No Local Chairperson seat with an account is published for {user.committee}. Seats come from data\YCP_USER_ROLES.csv through scripts\publish-committee-seats.js.</div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <select aria-label="Assignee uid" className="form-select" value={form.assigneeUid} onChange={e => setForm({ ...form, assigneeUid: e.target.value })}>
                  <option value="">To whom…</option>
                  {seats.filter(s => s.uid).map(s => <option key={s.uid} value={s.uid}>{s.name}{s.scopeKey ? ' (' + s.scopeKey + ')' : ''}</option>)}
                </select>
                <input aria-label="Due on" className="form-input" type="date" value={form.dueOn} onChange={e => setForm({ ...form, dueOn: e.target.value })} />
                <input aria-label="The assignment, in one line" className="form-input" style={{ gridColumn: '1 / span 2' }} placeholder="The assignment, in one line" value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} />
                <textarea aria-label="Detail (optional)" className="form-input" style={{ gridColumn: '1 / span 2' }} rows={3} placeholder="Detail (optional)" value={form.body} onChange={e => setForm({ ...form, body: e.target.value })} />
                <input aria-label="Claim id this concerns (optional)" className="form-input" placeholder="Claim id this concerns (optional)" value={form.subjectClaimId} onChange={e => setForm({ ...form, subjectClaimId: e.target.value })} />
                <button className="btn btn-primary btn-sm" disabled={saving} onClick={issue}>Issue</button>
              </div>
            )}
            {formErr.length ? <div style={{ color: C.bad, marginTop: 6 }}>{formErr.map((e, i) => <div key={i}>&#9888; {e}</div>)}</div> : null}
            {saved ? <div style={{ color: C.ok, marginTop: 6 }}>{saved}</div> : null}
          </div>
        )}

        {rows === null && !err ? <div>Loading…</div> : null}
        {rows && rows.length === 0 && !err ? <div>None on record.</div> : null}
        {p.open.map(x => <Row key={x.assignment.id} x={x} user={user} mode={mode} onOpenClaim={onOpenClaim} />)}
        {p.closed.length ? <details style={{ marginTop: 8 }}><summary style={{ cursor: 'pointer' }}>{p.closed.length} closed</summary>{p.closed.map(x => <Row key={x.assignment.id} x={x} user={user} mode={mode} onOpenClaim={onOpenClaim} />)}</details> : null}
      </div>
    </div>
  );
}
